import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Components from "../../../components";
import { FormContainer } from "../Shared";
import {
  useCreateUserGroupMutation,
  useUpdateUserGroupMutation,
  useGetAppsQuery,
  useGetUserGroupQuery,
  useGetRolesQuery,
} from "../services";
import { useForm, useDropDownSearch } from "../../../hooks";
import { userGroupsFormSchema } from "../validations";
import { mergeObjects } from "../../../shared/helper";
import { getLocalizedString } from "../../../shared/translation";
import { removeAllTrailingBlanks } from "../../../shared/utility";
import { getUserGroupFormValues } from "./formData";

export const UserGroupForm = ({ idToEditOrCsvUpload = null, onClose }) => {
  const { data: apps = {} } = useGetAppsQuery({ page: 1, per_page: 1000 });

  const [initialValues, setInitialValues] = useState({
    name: "",
    app_id: "",
    client_app_role_id: "",
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: userGroupsFormSchema,
    onSubmit: (value) => handleFormSubmit(value),
  });

  const { data = {} } = useGetUserGroupQuery(
    { id: idToEditOrCsvUpload },
    { skip: !idToEditOrCsvUpload }
  );

  // Get roles list for the single-select Role dropdown
  const { data: rolesList, setSearchKey: setRolesSearch } = useDropDownSearch({
    useData: useGetRolesQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.client_app_role_id,
  });

  // Set form data in case of edit
  useEffect(() => {
    if (idToEditOrCsvUpload && data) {
      setInitialValues(mergeObjects(initialValues, getUserGroupFormValues(data)));
    }
  }, [data]);

  const { create: createUserGroup, update: updateUserGroup } = useForm({
    createMutation: useCreateUserGroupMutation,
    updateMutation: useUpdateUserGroupMutation,
    closeForm: onClose,
    setError: formik.setErrors,
  });

  const handleFormSubmit = (formData) => {
    const cleanedFormData = removeAllTrailingBlanks(formData);
    if (idToEditOrCsvUpload) {
      updateUserGroup({ id: idToEditOrCsvUpload, formData: cleanedFormData });
    } else {
      createUserGroup({ formData: cleanedFormData });
    }
  };

  return (
    <FormContainer
      resourceName={getLocalizedString("user_group", "User Group")}
      closeForm={() => onClose(false)}
      handleFormSubmit={formik.handleSubmit}
      idToEdit={idToEditOrCsvUpload}
      bodyClassName="user-group-form"
    >
      <Components.QICustomSelect
        label={getLocalizedString("application", "Application")}
        {...formik.getFieldProps("app_id")}
        onChange={(value) => formik.setFieldValue("app_id", value)}
        error={formik.touched.app_id && formik.errors.app_id}
        disabled={idToEditOrCsvUpload}
        labelClassName="applications"
      >
        <>
          <li value="">All</li>
          {apps?.data?.map((app, index) => (
            <li value={app.id} key={index}>
              {app.name}
            </li>
          ))}
        </>
      </Components.QICustomSelect>

      <Components.QICustomSelect
        label={getLocalizedString("role", "Role")}
        {...formik.getFieldProps("client_app_role_id")}
        onChange={(value) => {
          formik.setFieldValue("client_app_role_id", value);
          setRolesSearch("");
        }}
        onSearch={setRolesSearch}
        error={formik.touched.client_app_role_id && formik.errors.client_app_role_id}
        labelClassName="user-group-role"
      >
        <>
          <li value="">{getLocalizedString("select", "Select")}</li>
          {rolesList?.data?.map((role, index) => (
            <li value={role.id} key={index}>
              {role.name}
            </li>
          ))}
        </>
      </Components.QICustomSelect>

      <Components.QIInput
        label={getLocalizedString("group_name", "Group Name")}
        {...formik.getFieldProps("name")}
        error={formik.touched.name && formik.errors.name}
        disabled={data?.created_by === "System" ? true : false}
      />
    </FormContainer>
  );
};
